import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PaperPageView } from "@/components/inkwell/PaperPageView";
import { getPaper, papers } from "@/lib/papers-data";
import logo from "@/assets/inkwell-logo.png";

export default function PaperReader() {
  const { id } = useParams<{ id: string }>();
  const paper = id ? getPaper(id) : undefined;
  const [index, setIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    setIndex(0);
    setPrevIndex(null);
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!paper) return;
    document.title = `${paper.title} — The Inkwell Papers, ${paper.issue}`;
    const desc = `${paper.standfirst}. ${paper.issue} of The Inkwell Papers — a journal from The Inkwell, a California telehealth therapy practice.`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content") || "";
    meta.setAttribute("content", desc);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const prevCanonical = link.getAttribute("href") || "";
    link.setAttribute("href", `https://theinkwelltherapy.com/papers/${paper.id}`);
    return () => {
      document.title = "The Inkwell | LMFT in California — Therapy for Individuals, Couples & Families";
      if (prevDesc) meta.setAttribute("content", prevDesc);
      if (prevCanonical) link.setAttribute("href", prevCanonical);
    };
  }, [paper]);

  if (!paper) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-inkblack px-6 text-center text-ivory">
        <p className="font-serif text-3xl font-light">This issue is unavailable.</p>
        <a
          href="/#papers"
          data-testid="papers-back-link"
          className="font-mono text-xs tracking-[0.22em] uppercase text-gold hover:text-ivory"
        >
          ← The Inkwell Papers
        </a>
      </main>
    );
  }

  const page = paper.pages[index];
  const go = (next: number) => {
    if (next === index || next < 0 || next >= paper.pages.length) return;
    if (!reduce) {
      setPrevIndex(index);
      window.setTimeout(() => setPrevIndex(null), 420);
    }
    setIndex(next);
    window.scrollTo({ top: 0 });
  };

  return (
    <main data-testid="papers-reader" className="min-h-screen bg-inkblack px-5 py-8 text-ivory md:px-10 md:py-12">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5">
          <a href="/" aria-label="The Inkwell home" className="block w-[130px]">
            <img src={logo} alt="The Inkwell — A Marriage and Family Therapy Practice" className="h-auto w-full" />
          </a>
          <div className="flex items-center gap-5">
            {papers.map((p) => (
              <Link
                key={p.id}
                to={`/papers/${p.id}`}
                data-testid={`papers-switch-${p.id}`}
                aria-current={p.id === paper.id ? "true" : undefined}
                className={`font-mono text-[0.65rem] tracking-[0.18em] uppercase transition-colors ${
                  p.id === paper.id ? "text-gold" : "text-stone/60 hover:text-ivory"
                }`}
              >
                {p.issue}
              </Link>
            ))}
            <a
              href="/#papers"
              data-testid="papers-back-link"
              className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-stone/60 transition-colors hover:text-ivory"
            >
              ← Home
            </a>
          </div>
        </div>

        <p className="mt-5 font-mono text-[0.65rem] tracking-[0.16em] uppercase text-stone/50">
          {paper.issue} · {paper.date}
        </p>

        <nav aria-label="Magazine pages" className="mt-5 flex flex-wrap gap-2">
          {paper.pages.map((item, itemIndex) => (
            <button
              key={item.n}
              type="button"
              onClick={() => go(itemIndex)}
              data-testid={`papers-page-nav-${item.n}`}
              aria-current={itemIndex === index ? "page" : undefined}
              aria-label={`Page ${item.n}: ${item.label}`}
              className={`flex h-11 min-w-11 items-center justify-center border px-3 text-xs transition-colors ${
                itemIndex === index
                  ? "border-gold bg-gold text-inkblack"
                  : "border-white/10 text-stone/60 hover:text-ivory"
              }`}
            >
              {item.n}
            </button>
          ))}
        </nav>

        <div className="relative">
          {prevIndex !== null && paper.pages[prevIndex] && (
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
            >
              <PaperPageView page={paper.pages[prevIndex]} total={paper.pages.length} />
            </motion.div>
          )}
          <motion.div
            key={`${paper.id}-${index}`}
            initial={prevIndex !== null ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            <PaperPageView page={page} total={paper.pages.length} />
          </motion.div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => go(index - 1)}
            data-testid="papers-prev-button"
            className="flex min-h-12 items-center justify-center gap-2 border border-white/10 px-3 font-mono text-xs uppercase text-ivory transition-colors hover:border-ivory/40 disabled:opacity-35"
          >
            <ChevronLeft size={16} aria-hidden="true" /> Previous
          </button>
          <button
            type="button"
            disabled={index === paper.pages.length - 1}
            onClick={() => go(index + 1)}
            data-testid="papers-next-button"
            className="flex min-h-12 items-center justify-center gap-2 border border-gold px-3 font-mono text-xs uppercase text-gold transition-colors hover:bg-gold hover:text-inkblack disabled:opacity-35"
          >
            Next <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>

        <p className="mt-10 text-center text-xs text-stone/50">
          Continue the reflection —{" "}
          <a
            href="/the-inkwell-journal.pdf"
            download="The-Inkwell-Journal.pdf"
            data-testid="reader-journal-link"
            className="text-stone/70 underline decoration-white/20 underline-offset-4 transition-colors hover:text-gold"
          >
            The Inkwell Journal
          </a>{" "}
          is a free space to write.
        </p>
      </div>
    </main>
  );
}
